import Phaser from 'phaser';
import { ASSET_KEYS, COLORS, FONTS, GAME_HEIGHT, GAME_WIDTH, fontStr } from '@/config';
import { gameState } from '@/state/store';
import { STAT_LABEL } from '@/data/courseCatalog';
import type { AuthorStats, Course } from '@/data/types';
import {
  SKRIVELAB_MAX_ORD,
  SKRIVELAB_MIN_ORD,
  SKRIVELAB_OMDOMME_BELONNING,
  analyserTekst,
  tellOrd,
  type SkrivelabAnalyse,
} from '@/systems/skrivelab';
import {
  genererNpcVurdering,
  npcNavn,
  type VurderingsResultat,
  type VurderingsStemme,
} from '@/systems/npcVurdering';
import { animateModalGlide } from '@/ui/modalGlide';

const SHEET_WIDTH = 1000;
const SHEET_X = (GAME_WIDTH - SHEET_WIDTH) / 2;
const SHEET_Y = 96;
const SHEET_HEIGHT = GAME_HEIGHT - 192;
const PADDING = 28;

const STAT_KEYS: ReadonlyArray<keyof AuthorStats> = [
  'dialog',
  'plot',
  'karakter',
  'spraak',
  'voice',
  'autentisitet',
  'disiplin',
];

const ANALYSE_DELAY_MS = 3000;

type Modus = 'skriv' | 'analyserer' | 'resultat';

type StemmeValg = Extract<VurderingsStemme, 'kristine' | 'bjorn'>;

export class SkrivelabModal {
  private layer?: Phaser.GameObjects.Container;
  private domWrapper?: HTMLDivElement;
  private textarea?: HTMLTextAreaElement;
  private modus: Modus = 'skriv';
  private siste?: SkrivelabAnalyse;
  private sisteVurdering?: VurderingsResultat;
  private valgtStemme: StemmeValg = 'kristine';
  private feilmelding = '';
  private unsubscribe?: () => void;
  private analyseTimer?: Phaser.Time.TimerEvent;
  private fadeTweens: Phaser.Tweens.Tween[] = [];

  constructor(private scene: Phaser.Scene) {}

  get isOpen(): boolean {
    return !!this.layer;
  }

  open(): void {
    if (this.layer) return;
    this.modus = 'skriv';
    this.siste = undefined;
    this.sisteVurdering = undefined;
    this.feilmelding = '';

    this.layer = this.scene.add.container(0, 0).setDepth(140);
    const backdrop = this.scene.add
      .rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.6)
      .setOrigin(0, 0)
      .setInteractive();
    this.layer.add(backdrop);

    this.byggDom();
    this.render();
    animateModalGlide(this.scene, this.layer);

    this.unsubscribe = gameState.subscribe(() => {
      if (this.modus === 'skriv' || this.modus === 'resultat') {
        this.render();
      }
    });
  }

  close(): void {
    this.unsubscribe?.();
    this.unsubscribe = undefined;
    this.analyseTimer?.remove(false);
    this.analyseTimer = undefined;
    this.stoppTweens();
    this.fjernDom();
    this.layer?.destroy(true);
    this.layer = undefined;
    this.modus = 'skriv';
    this.siste = undefined;
    this.sisteVurdering = undefined;
  }

  private byggDom(): void {
    const canvas = this.scene.game.canvas;
    const parent = canvas.parentElement;
    if (!parent) return;
    parent.style.position = parent.style.position || 'relative';

    const wrapper = document.createElement('div');
    wrapper.style.position = 'absolute';
    wrapper.style.left = '0';
    wrapper.style.top = '0';
    wrapper.style.width = '100%';
    wrapper.style.height = '100%';
    wrapper.style.pointerEvents = 'none';
    wrapper.style.zIndex = '50';
    parent.appendChild(wrapper);
    this.domWrapper = wrapper;

    const textarea = document.createElement('textarea');
    textarea.placeholder = 'Skriv eller lim inn 50–200 ord her …';
    textarea.spellcheck = true;
    textarea.style.position = 'absolute';
    textarea.style.boxSizing = 'border-box';
    textarea.style.padding = '16px';
    textarea.style.fontFamily = FONTS.body;
    // Responsiv font: skalerer med viewport men aldri under 18px (runde 31)
    textarea.style.fontSize = 'min(4.4vw, 26px)';
    textarea.style.lineHeight = '1.5';
    textarea.style.background = '#ffffff';
    textarea.style.color = '#1a1a1a';
    textarea.style.border = '2px solid #862736';
    textarea.style.borderRadius = '6px';
    textarea.style.resize = 'none';
    textarea.style.outline = 'none';
    textarea.style.pointerEvents = 'auto';
    textarea.addEventListener('input', () => this.render());
    wrapper.appendChild(textarea);
    this.textarea = textarea;

    this.plasserTextarea();
    window.addEventListener('resize', this.onWindowResize);
  }

  private fjernDom(): void {
    window.removeEventListener('resize', this.onWindowResize);
    this.textarea?.remove();
    this.textarea = undefined;
    this.domWrapper?.remove();
    this.domWrapper = undefined;
  }

  private onWindowResize = (): void => {
    this.plasserTextarea();
  };

  private plasserTextarea(): void {
    if (!this.textarea || !this.domWrapper) return;
    const canvas = this.scene.game.canvas;
    const parent = canvas.parentElement;
    if (!parent) return;
    const rect = canvas.getBoundingClientRect();
    const parentRect = parent.getBoundingClientRect();
    const skala = rect.width / GAME_WIDTH;
    const offsetX = rect.left - parentRect.left;
    const offsetY = rect.top - parentRect.top;

    const taX = SHEET_X + PADDING;
    const taY = SHEET_Y + 220;
    const taW = SHEET_WIDTH - PADDING * 2;
    const taH = 360;

    this.textarea.style.left = `${offsetX + taX * skala}px`;
    this.textarea.style.top = `${offsetY + taY * skala}px`;
    this.textarea.style.width = `${taW * skala}px`;
    this.textarea.style.height = `${taH * skala}px`;
    // Skala følger spillets internal-størrelse; gulvet er nå 18px for lesbarhet (runde 31)
    this.textarea.style.fontSize = `${Math.max(18, Math.round(22 * skala))}px`;
    this.textarea.style.display = this.modus === 'skriv' ? 'block' : 'none';
  }

  private render(): void {
    if (!this.layer) return;
    while (this.layer.list.length > 1) {
      const last = this.layer.list[this.layer.list.length - 1];
      this.layer.remove(last, true);
    }
    this.stoppTweens();

    this.byggSheet();
    this.byggHeader();

    if (this.modus === 'skriv') {
      this.byggSkriveModus();
    } else if (this.modus === 'analyserer') {
      this.byggAnalyserer();
    } else {
      this.byggResultat();
    }

    this.byggBunnTeller();
    this.plasserTextarea();
  }

  private byggSheet(): void {
    if (!this.layer) return;
    const sheet = this.scene.add
      .rectangle(SHEET_X, SHEET_Y, SHEET_WIDTH, SHEET_HEIGHT, COLORS.beige, 0.98)
      .setOrigin(0, 0)
      .setStrokeStyle(3, COLORS.wine, 0.6);
    this.layer.add(sheet);

    const topBorder = this.scene.add
      .rectangle(SHEET_X, SHEET_Y, SHEET_WIDTH, 4, COLORS.wine, 1)
      .setOrigin(0, 0);
    this.layer.add(topBorder);
  }

  private byggHeader(): void {
    if (!this.layer) return;
    const header = this.scene.add
      .text(SHEET_X + PADDING, SHEET_Y + 32, 'Skrivelab', {
        fontFamily: FONTS.display,
        fontSize: fontStr(54),
        color: '#862736',
        wordWrap: { width: SHEET_WIDTH - PADDING * 2 - 110 },
      })
      .setOrigin(0, 0);
    this.layer.add(header);

    const undertittel = this.scene.add
      .text(
        SHEET_X + PADDING,
        SHEET_Y + 120,
        'Skriv eller lim inn 50–200 ord. Få en redaktørs øyne på teksten din.',
        {
          fontFamily: FONTS.body,
          fontSize: fontStr(20),
          color: '#5a4a3a',
          wordWrap: { width: SHEET_WIDTH - PADDING * 2 - 110 },
        },
      )
      .setOrigin(0, 0);
    this.layer.add(undertittel);

    const closeBtn = this.scene.add
      .text(SHEET_X + SHEET_WIDTH - PADDING, SHEET_Y + 4, '×', {
        fontFamily: FONTS.display,
        fontSize: fontStr(72),
        color: '#1a1a1a',
      })
      .setOrigin(1, 0)
      .setInteractive({ useHandCursor: true });
    closeBtn.on('pointerup', () => this.close());
    this.layer.add(closeBtn);
  }

  private byggSkriveModus(): void {
    if (!this.layer) return;
    const tekst = this.textarea?.value ?? '';
    const ordtelling = tellOrd(tekst);

    const tellerY = SHEET_Y + 220 + 360 + 12;
    const tellerFarge =
      ordtelling >= SKRIVELAB_MIN_ORD && ordtelling <= SKRIVELAB_MAX_ORD
        ? '#3d5a4d'
        : '#862736';
    const teller = this.scene.add
      .text(SHEET_X + PADDING, tellerY, `${ordtelling} / ${SKRIVELAB_MAX_ORD} ord`, {
        fontFamily: FONTS.hud,
        fontSize: fontStr(20),
        color: tellerFarge,
        fontStyle: '700',
      })
      .setOrigin(0, 0);
    this.layer.add(teller);

    if (this.feilmelding) {
      const feil = this.scene.add
        .text(SHEET_X + SHEET_WIDTH - PADDING, tellerY, this.feilmelding, {
          fontFamily: FONTS.body,
          fontSize: fontStr(18),
          color: '#862736',
          fontStyle: 'italic',
        })
        .setOrigin(1, 0);
      this.layer.add(feil);
    }

    const stemmeY = tellerY + 40;
    this.byggStemmeValg(stemmeY);

    const knappW = 460;
    const knappH = 72;
    const knappX = SHEET_X + (SHEET_WIDTH - knappW) / 2;
    const knappY = stemmeY + 96;

    const kanAnalysere =
      ordtelling >= SKRIVELAB_MIN_ORD && ordtelling <= SKRIVELAB_MAX_ORD;
    const knappFarge = kanAnalysere ? COLORS.wine : 0xcfc6b7;
    const knappTekstFarge = kanAnalysere ? '#ffffff' : '#7a7060';
    const knappTekst = !kanAnalysere
      ? ordtelling < SKRIVELAB_MIN_ORD
        ? `Trenger minst ${SKRIVELAB_MIN_ORD} ord`
        : `Maks ${SKRIVELAB_MAX_ORD} ord`
      : `Få tilbakemelding fra ${npcNavn(this.valgtStemme)} (gratis)`;

    const knappBg = this.scene.add
      .rectangle(knappX, knappY, knappW, knappH, knappFarge, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(2, COLORS.ink, 0.2);
    const knappTxt = this.scene.add
      .text(knappX + knappW / 2, knappY + knappH / 2, knappTekst, {
        fontFamily: FONTS.hud,
        fontSize: fontStr(22),
        color: knappTekstFarge,
        fontStyle: '700',
      })
      .setOrigin(0.5);
    this.layer.add([knappBg, knappTxt]);

    if (kanAnalysere) {
      knappBg.setInteractive({ useHandCursor: true });
      knappBg.on('pointerover', () => knappBg.setAlpha(0.85));
      knappBg.on('pointerout', () => knappBg.setAlpha(1));
      knappBg.on('pointerup', () => this.startAnalyse(tekst));
    }
  }

  private byggStemmeValg(y: number): void {
    if (!this.layer) return;
    const label = this.scene.add
      .text(SHEET_X + PADDING, y, 'Hvem skal lese?', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(18),
        color: '#5a4a3a',
        fontStyle: '600',
      })
      .setOrigin(0, 0);
    this.layer.add(label);

    const knappH = 56;
    const knappW = 180;
    const gap = 12;
    const startX = SHEET_X + PADDING + label.width + 20;
    const knappY = y - 12;

    type KnappDef = {
      stemme: StemmeValg | 'haldis';
      tekst: string;
      aktiv: boolean;
      hint?: string;
    };
    const knapper: ReadonlyArray<KnappDef> = [
      { stemme: 'kristine', tekst: 'Kristine', aktiv: true },
      { stemme: 'bjorn', tekst: 'Bjørn', aktiv: true },
      { stemme: 'haldis', tekst: 'Haldis', aktiv: false, hint: 'Krever ansatt redaktør' },
    ];

    let cx = startX;
    for (const k of knapper) {
      const valgt = k.aktiv && this.valgtStemme === k.stemme;
      const farge = valgt ? COLORS.ochre : k.aktiv ? COLORS.white : 0xe6dfd0;
      const tekstFarge = k.aktiv ? '#1a1a1a' : '#9a9080';
      const strekFarge = valgt ? COLORS.wine : COLORS.oakDark;
      const bg = this.scene.add
        .rectangle(cx, knappY, knappW, knappH, farge, 1)
        .setOrigin(0, 0)
        .setStrokeStyle(valgt ? 3 : 1, strekFarge, valgt ? 1 : 0.4);
      const txt = this.scene.add
        .text(cx + knappW / 2, knappY + knappH / 2, k.tekst, {
          fontFamily: FONTS.hud,
          fontSize: fontStr(18),
          color: tekstFarge,
          fontStyle: valgt ? '700' : '600',
        })
        .setOrigin(0.5);
      this.layer.add([bg, txt]);

      if (k.aktiv && k.stemme !== 'haldis') {
        bg.setInteractive({ useHandCursor: true });
        bg.on('pointerup', () => {
          this.valgtStemme = k.stemme as StemmeValg;
          this.render();
        });
      } else if (k.hint) {
        bg.setInteractive({ useHandCursor: false });
        const hintTxt = this.scene.add
          .text(cx + knappW / 2, knappY + knappH + 10, k.hint, {
            fontFamily: FONTS.body,
            fontSize: fontStr(11),
            color: '#7a7060',
            fontStyle: 'italic',
            wordWrap: { width: knappW + 60 },
            align: 'center',
          })
          .setOrigin(0.5, 0);
        this.layer.add(hintTxt);
      }
      cx += knappW + gap;
    }
  }

  private startAnalyse(tekst: string): void {
    const resultat = analyserTekst(tekst);
    if (!resultat.ok) {
      this.feilmelding =
        resultat.feil.type === 'for-kort'
          ? `Trenger minst ${SKRIVELAB_MIN_ORD} ord`
          : resultat.feil.type === 'for-lang'
            ? `Maks ${SKRIVELAB_MAX_ORD} ord`
            : 'Skriv noe først';
      this.render();
      return;
    }
    this.feilmelding = '';
    this.modus = 'analyserer';
    this.render();

    this.analyseTimer = this.scene.time.delayedCall(ANALYSE_DELAY_MS, () => {
      this.siste = resultat.analyse;
      this.sisteVurdering = genererNpcVurdering(
        resultat.analyse,
        this.valgtStemme,
        undefined,
        gameState.spillerForfatter.navn,
      );
      this.modus = 'resultat';
      this.analyseTimer = undefined;
      this.render();
    });
  }

  private byggAnalyserer(): void {
    if (!this.layer) return;
    const senterY = SHEET_Y + SHEET_HEIGHT / 2 - 40;
    const tekst = this.scene.add
      .text(SHEET_X + SHEET_WIDTH / 2, senterY, 'Analyserer …', {
        fontFamily: FONTS.display,
        fontSize: fontStr(44),
        color: '#862736',
      })
      .setOrigin(0.5);
    this.layer.add(tekst);

    const sub = this.scene.add
      .text(
        SHEET_X + SHEET_WIDTH / 2,
        senterY + 64,
        `${npcNavn(this.valgtStemme)} leser gjennom teksten din …`,
        {
          fontFamily: FONTS.body,
          fontSize: fontStr(20),
          color: '#5a4a3a',
          fontStyle: 'italic',
        },
      )
      .setOrigin(0.5);
    this.layer.add(sub);

    const dot = this.scene.add.circle(SHEET_X + SHEET_WIDTH / 2, senterY + 120, 14, COLORS.wine, 1);
    this.layer.add(dot);
    const pulse = this.scene.tweens.add({
      targets: dot,
      alpha: { from: 1, to: 0.2 },
      duration: 700,
      yoyo: true,
      repeat: -1,
    });
    this.fadeTweens.push(pulse);
  }

  private byggResultat(): void {
    if (!this.layer || !this.siste) return;
    const startY = SHEET_Y + 220;
    const innholdsBredde = SHEET_WIDTH - PADDING * 2;
    const innholdsX = SHEET_X + PADDING;

    const radHoyde = 44;
    let y = startY;

    const statHeader = this.scene.add
      .text(innholdsX, y, 'Redaktørens vurdering', {
        fontFamily: FONTS.display,
        fontSize: fontStr(28),
        color: '#1a1a1a',
      })
      .setOrigin(0, 0);
    this.layer.add(statHeader);
    y += 48;

    for (const key of STAT_KEYS) {
      const verdi = this.siste.stats[key];
      this.byggStatRad(innholdsX, y, innholdsBredde, key, verdi);
      y += radHoyde;
    }

    y += 24;
    if (this.sisteVurdering) {
      this.byggNpcTilbakemelding(innholdsX, y, innholdsBredde, this.sisteVurdering);
      y += 270;
    } else {
      this.byggTilbakemeldingsboks(innholdsX, y, innholdsBredde, this.siste.tilbakemelding);
      y += 220;
    }

    if (this.siste.anbefaltKurs) {
      this.byggKursKort(innholdsX, y, innholdsBredde, this.siste.anbefaltKurs);
      y += 180;
    } else {
      const balanseTxt = this.scene.add
        .text(
          innholdsX,
          y,
          'Du har bra balanse — bare fortsett å skrive.',
          {
            fontFamily: FONTS.body,
            fontSize: fontStr(20),
            color: '#3d5a4d',
            fontStyle: 'italic',
            wordWrap: { width: innholdsBredde },
          },
        )
        .setOrigin(0, 0);
      this.layer.add(balanseTxt);
      y += 56;
    }

    const knappW = 540;
    const knappH = 84;
    const knappX = SHEET_X + (SHEET_WIDTH - knappW) / 2;
    const knappY = SHEET_Y + SHEET_HEIGHT - knappH - 64;

    const knappBg = this.scene.add
      .rectangle(knappX, knappY, knappW, knappH, COLORS.wine, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(2, COLORS.ink, 0.2);
    const knappTxt = this.scene.add
      .text(
        knappX + knappW / 2,
        knappY + knappH / 2,
        `Lukk og motta belønning (+${SKRIVELAB_OMDOMME_BELONNING} omdømme)`,
        {
          fontFamily: FONTS.hud,
          fontSize: fontStr(22),
          color: '#ffffff',
          fontStyle: '700',
        },
      )
      .setOrigin(0.5);
    this.layer.add([knappBg, knappTxt]);

    knappBg.setInteractive({ useHandCursor: true });
    knappBg.on('pointerover', () => knappBg.setAlpha(0.85));
    knappBg.on('pointerout', () => knappBg.setAlpha(1));
    knappBg.on('pointerup', () => {
      gameState.loggSkrivelabBruk(this.siste);
      this.close();
    });
  }

  private byggStatRad(
    x: number,
    y: number,
    bredde: number,
    key: keyof AuthorStats,
    verdi: number,
  ): void {
    if (!this.layer) return;
    const labelW = 200;
    const valueW = 60;
    const barX = x + labelW;
    const barW = bredde - labelW - valueW - 12;
    const barH = 14;

    const label = this.scene.add
      .text(x, y, STAT_LABEL[key], {
        fontFamily: FONTS.body,
        fontSize: fontStr(20),
        color: '#1a1a1a',
        fontStyle: '600',
      })
      .setOrigin(0, 0);
    this.layer.add(label);

    const bg = this.scene.add
      .rectangle(barX, y + 14, barW, barH, COLORS.beigeDark, 1)
      .setOrigin(0, 0);
    this.layer.add(bg);

    const fyll = this.scene.add
      .rectangle(barX, y + 14, 1, barH, COLORS.wine, 1)
      .setOrigin(0, 0);
    this.layer.add(fyll);

    const maaltBredde = Math.max(2, Math.round((barW * verdi) / 100));
    const tween = this.scene.tweens.add({
      targets: fyll,
      width: maaltBredde,
      duration: 480,
      ease: 'Cubic.easeOut',
    });
    this.fadeTweens.push(tween);

    const verdiTxt = this.scene.add
      .text(x + bredde, y + 4, String(verdi), {
        fontFamily: FONTS.hud,
        fontSize: fontStr(20),
        color: '#1a1a1a',
        fontStyle: '700',
      })
      .setOrigin(1, 0);
    this.layer.add(verdiTxt);
  }

  private byggTilbakemeldingsboks(
    x: number,
    y: number,
    bredde: number,
    tekst: string,
  ): void {
    if (!this.layer) return;
    const hoyde = 220;
    const bg = this.scene.add
      .rectangle(x, y, bredde, hoyde, COLORS.white, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(1, COLORS.ink, 0.15);
    this.layer.add(bg);

    const venstreBorder = this.scene.add
      .rectangle(x, y, 4, hoyde, COLORS.ochre, 1)
      .setOrigin(0, 0);
    this.layer.add(venstreBorder);

    const txt = this.scene.add
      .text(x + 20, y + 20, tekst, {
        fontFamily: FONTS.body,
        fontSize: fontStr(20),
        color: '#1a1a1a',
        fontStyle: 'italic',
        wordWrap: { width: bredde - 40 },
        lineSpacing: 6,
      })
      .setOrigin(0, 0)
      .setAlpha(0);
    this.layer.add(txt);

    const tween = this.scene.tweens.add({
      targets: txt,
      alpha: 1,
      duration: 480,
      ease: 'Cubic.easeOut',
    });
    this.fadeTweens.push(tween);
  }

  private byggNpcTilbakemelding(
    x: number,
    y: number,
    bredde: number,
    vurdering: VurderingsResultat,
  ): void {
    if (!this.layer) return;
    const hoyde = 260;
    const bg = this.scene.add
      .rectangle(x, y, bredde, hoyde, COLORS.white, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(1, COLORS.ink, 0.15);
    this.layer.add(bg);

    const venstreBorder = this.scene.add
      .rectangle(x, y, 4, hoyde, COLORS.wine, 1)
      .setOrigin(0, 0);
    this.layer.add(venstreBorder);

    const portrettX = x + 24;
    const portrettY = y + 20;
    const portrettStr = 64;
    const harBilde =
      vurdering.stemme === 'egen-forfatter' &&
      this.scene.textures.exists(ASSET_KEYS.playerSven);

    if (harBilde) {
      const bilde = this.scene.add
        .image(portrettX + portrettStr / 2, portrettY + portrettStr / 2, ASSET_KEYS.playerSven)
        .setDisplaySize(portrettStr, portrettStr);
      const maskGfx = this.scene.make.graphics({});
      maskGfx.fillStyle(0xffffff);
      maskGfx.fillCircle(
        portrettX + portrettStr / 2,
        portrettY + portrettStr / 2,
        portrettStr / 2,
      );
      const mask = maskGfx.createGeometryMask();
      bilde.setMask(mask);
      this.layer.add(bilde);
    } else {
      const initial = vurdering.npcNavn.charAt(0);
      const sirkel = this.scene.add
        .circle(
          portrettX + portrettStr / 2,
          portrettY + portrettStr / 2,
          portrettStr / 2,
          COLORS.ochre,
          1,
        )
        .setStrokeStyle(2, COLORS.wine, 0.8);
      this.layer.add(sirkel);
      const initialTxt = this.scene.add
        .text(
          portrettX + portrettStr / 2,
          portrettY + portrettStr / 2,
          initial,
          {
            fontFamily: FONTS.display,
            fontSize: fontStr(32),
            color: '#1a1a1a',
            fontStyle: '700',
          },
        )
        .setOrigin(0.5);
      this.layer.add(initialTxt);
    }

    const navnX = portrettX + portrettStr + 16;
    const navn = this.scene.add
      .text(navnX, portrettY + 4, vurdering.npcNavn, {
        fontFamily: FONTS.display,
        fontSize: fontStr(22),
        color: '#1a1a1a',
        wordWrap: { width: bredde - navnX + x - 24 },
      })
      .setOrigin(0, 0);
    this.layer.add(navn);

    const rolle = this.scene.add
      .text(navnX, portrettY + 42, vurdering.npcRolle, {
        fontFamily: FONTS.body,
        fontSize: fontStr(14),
        color: '#7a7060',
        fontStyle: 'italic',
        wordWrap: { width: bredde - navnX + x - 24 },
      })
      .setOrigin(0, 0);
    this.layer.add(rolle);

    const tekstY = portrettY + portrettStr + 16;
    const tekstBredde = bredde - 48;
    const hilsen = this.scene.add
      .text(x + 24, tekstY, vurdering.hilsen.trim(), {
        fontFamily: FONTS.body,
        fontSize: fontStr(18),
        color: '#1a1a1a',
        fontStyle: '700 italic',
        wordWrap: { width: tekstBredde },
      })
      .setOrigin(0, 0);
    this.layer.add(hilsen);

    const innholdY = tekstY + hilsen.height + 4;
    const innhold = this.scene.add
      .text(
        x + 24,
        innholdY,
        `${vurdering.tilbakemelding} ${vurdering.avslutning}`,
        {
          fontFamily: FONTS.body,
          fontSize: fontStr(17),
          color: '#1a1a1a',
          fontStyle: 'italic',
          wordWrap: { width: tekstBredde },
          lineSpacing: 4,
        },
      )
      .setOrigin(0, 0)
      .setAlpha(0);
    this.layer.add(innhold);

    const tween = this.scene.tweens.add({
      targets: innhold,
      alpha: 1,
      duration: 480,
      ease: 'Cubic.easeOut',
    });
    this.fadeTweens.push(tween);
  }

  private byggKursKort(x: number, y: number, bredde: number, kurs: Course): void {
    if (!this.layer) return;
    const hoyde = 160;
    const bg = this.scene.add
      .rectangle(x, y, bredde, hoyde, COLORS.white, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(2, COLORS.oakDark, 0.4);
    this.layer.add(bg);

    const tag = this.scene.add
      .text(x + 20, y + 16, 'ANBEFALT KURS', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(14),
        color: '#862736',
        fontStyle: '700',
      })
      .setOrigin(0, 0);
    this.layer.add(tag);

    const tittel = this.scene.add
      .text(x + 20, y + 48, kurs.title, {
        fontFamily: FONTS.display,
        fontSize: fontStr(24),
        color: '#1a1a1a',
        wordWrap: { width: bredde - 280 },
      })
      .setOrigin(0, 0);
    this.layer.add(tittel);

    const pris = this.scene.add
      .text(x + 20, y + hoyde - 36, `${kurs.pris.toLocaleString('nb-NO')} kr`, {
        fontFamily: FONTS.hud,
        fontSize: fontStr(18),
        color: '#5a4a3a',
        fontStyle: '600',
      })
      .setOrigin(0, 0);
    this.layer.add(pris);

    const knappW = 240;
    const knappH = 66;
    const knappX = x + bredde - knappW - 16;
    const knappY = y + (hoyde - knappH) / 2;
    const knappBg = this.scene.add
      .rectangle(knappX, knappY, knappW, knappH, COLORS.ochre, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(2, COLORS.ink, 0.2);
    const knappTxt = this.scene.add
      .text(knappX + knappW / 2, knappY + knappH / 2, 'Se kurset', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(18),
        color: '#1a1a1a',
        fontStyle: '700',
      })
      .setOrigin(0.5);
    this.layer.add([knappBg, knappTxt]);

    if (kurs.url) {
      knappBg.setInteractive({ useHandCursor: true });
      knappBg.on('pointerover', () => knappBg.setAlpha(0.85));
      knappBg.on('pointerout', () => knappBg.setAlpha(1));
      knappBg.on('pointerup', () => {
        if (typeof window !== 'undefined') {
          window.open(kurs.url, '_blank', 'noopener,noreferrer');
        }
      });
    }
  }

  private byggBunnTeller(): void {
    if (!this.layer) return;
    const brukt = gameState.skrivelabBrukt;
    const tekst =
      brukt === 0
        ? 'Du har ikke brukt Skrivelab ennå.'
        : brukt === 1
          ? 'Du har brukt Skrivelab 1 gang.'
          : `Du har brukt Skrivelab ${brukt} ganger.`;
    const txt = this.scene.add
      .text(SHEET_X + SHEET_WIDTH / 2, SHEET_Y + SHEET_HEIGHT - 28, tekst, {
        fontFamily: FONTS.hud,
        fontSize: fontStr(16),
        color: '#7a7060',
        fontStyle: 'italic',
      })
      .setOrigin(0.5);
    this.layer.add(txt);
  }

  private stoppTweens(): void {
    for (const t of this.fadeTweens) t.stop();
    this.fadeTweens = [];
  }
}
